import React from 'react';
import clsx from 'clsx';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'danger'
  | 'destructive'
  | 'success';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leadingIcon?: React.ReactNode;
}

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-primary-600 text-white border-primary-600 hover:bg-primary-700 hover:border-primary-700 shadow-sm',
  secondary:
    'bg-primary-50 text-primary-800 border-primary-100 hover:bg-primary-100 dark:bg-primary-950 dark:text-primary-200 dark:border-primary-900 dark:hover:bg-primary-900',
  outline:
    'bg-white text-brand-navy border-secondary-300 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 dark:bg-secondary-900 dark:text-white dark:border-secondary-700 dark:hover:bg-secondary-800',
  ghost:
    'bg-transparent text-secondary-600 border-transparent hover:bg-secondary-100 hover:text-secondary-900 dark:text-secondary-300 dark:hover:bg-secondary-800 dark:hover:text-white',
  danger:
    'bg-danger-600 text-white border-danger-600 hover:bg-danger-700 hover:border-danger-700',
  destructive:
    'bg-danger-600 text-white border-danger-600 hover:bg-danger-700 hover:border-danger-700',
  success:
    'bg-success-600 text-white border-success-600 hover:bg-success-700 hover:border-success-700',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'min-h-9 px-3 text-sm',
  md: 'min-h-10 px-4 text-sm',
  lg: 'min-h-12 px-5 text-base',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      leadingIcon,
      disabled,
      children,
      type = 'button',
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-lg border font-semibold transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:ring-offset-secondary-950',
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {loading ? (
        <span
          className='h-4 w-4 animate-spin rounded-full border-2 border-current border-l-transparent'
          aria-hidden='true'
        />
      ) : (
        leadingIcon
      )}
      <span>{children}</span>
    </button>
  )
);

Button.displayName = 'Button';

export default Button;
